import React, { createContext, useContext, useState, useCallback } from 'react';

interface ParsedOrder {
  ouvrages: Array<{
    name: string;
    building?: string;
    level?: string;
    volume: number;
  }>;
  formula?: {
    name: string;
    strength?: string;
    exposure_class?: string;
    consistency?: string;
  };
  options?: string[];
  heure?: string;
  date?: string;
  comment?: string;
  confidence: number;
}

interface VoiceOrderContextType {
  isVoiceModalOpen: boolean;
  openVoiceModal: () => void;
  closeVoiceModal: () => void;
  parsedOrder: ParsedOrder | null;
  transcription: string;
  setParsedResult: (order: ParsedOrder | null, transcription: string) => void;
  clearParsedResult: () => void;
}

const VoiceOrderContext = createContext<VoiceOrderContextType | undefined>(undefined);

export function VoiceOrderProvider({ children }: { children: React.ReactNode }) {
  const [isVoiceModalOpen, setIsVoiceModalOpen] = useState(false);
  const [parsedOrder, setParsedOrder] = useState<ParsedOrder | null>(null);
  const [transcription, setTranscription] = useState('');

  const openVoiceModal = useCallback(() => {
    setIsVoiceModalOpen(true);
  }, []);

  const closeVoiceModal = useCallback(() => {
    setIsVoiceModalOpen(false);
  }, []);

  const setParsedResult = useCallback((order: ParsedOrder | null, text: string) => {
    setParsedOrder(order);
    setTranscription(text);
  }, []);

  const clearParsedResult = useCallback(() => {
    setParsedOrder(null);
    setTranscription('');
  }, []);

  return (
    <VoiceOrderContext.Provider value={{
      isVoiceModalOpen,
      openVoiceModal,
      closeVoiceModal,
      parsedOrder,
      transcription,
      setParsedResult,
      clearParsedResult,
    }}>
      {children}
    </VoiceOrderContext.Provider>
  );
}

export function useVoiceOrder() {
  const context = useContext(VoiceOrderContext);
  if (!context) {
    throw new Error('useVoiceOrder must be used within a VoiceOrderProvider');
  }
  return context;
}
